import React from 'react'
import {useController} from '@data-client/react'
import {apiReply, toast, useForm} from '../lib'
import {ActionHeader, AppMsg, Field, RequiredFieldNote, SubmitButton} from '../components'
import {useSearchParam} from 'react-use'
import {SignInResource} from '../lib/model'
import {RecoverSignInSchema, ResetPasswordSchema} from '../lib/schema/Profile'
import {SignInForm} from '../lib/form/Profile'
import {Paper, Container, Typography, Box} from '@mui/material'

type RecoverPasswordProps = {
  expired: boolean
}
export function RecoverPassword({expired}: RecoverPasswordProps) {
  const api = useController()
  const [sent, delivered] = React.useState(false)
  const form = useForm(RecoverSignInSchema)

  const onSubmit = form.handleSubmit(async ({email}) => {
    const msg = toast('loading', 'Recovering Password')
    const reply = await apiReply(() => api.fetch(SignInResource.update, {id: 0}, {email}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    delivered(true)
    msg.remove()
  })

  return (
    <Container maxWidth="sm">
      <ActionHeader title="Password Reset"/>
      {expired && !sent && (
        <AppMsg severity="warning" size="medium">
          Token expired.<br/>Request a new token.
        </AppMsg>
      )}
      {sent && (
        <AppMsg size="medium">
          Password Instructions<br/>Will Be Emailed
        </AppMsg>
      )}
      <Paper elevation={3} sx={{py: 3, px: 2}}>
        <RequiredFieldNote />
        <form onSubmit={onSubmit}>
          <Field form={form} attrs={SignInForm.recover} />
          <Box mt={3} textAlign="center">
            <SubmitButton form={form}>Reset Password</SubmitButton>
          </Box>
        </form>
      </Paper>
    </Container>
  )
}

export default function ResetPasswordPage() {
  const api = useController()
  const form = useForm(ResetPasswordSchema)
  const token = useSearchParam('token') || ''
  const [expired, setExpired] = React.useState(false)

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: ResetPasswordSchema) => {
    const msg = toast('loading', 'Resetting Password')
    const reply = await apiReply(() => api.fetch(SignInResource.update, {id: 0}, {token, password: data.password}))
    if (reply.error) {
      if (reply.error === 'token expired') {
        setExpired(true)
        msg.remove()
      } else {
        msg.fail(reply.error)
      }
      return
    }
    msg.succeed()
    window.location.assign('/')
  })

  if (!token || expired) {
    return <RecoverPassword expired={expired} />
  }
  return (
    <Container maxWidth="md">
      <Typography variant="h1" my={3}>
        Password Reset
      </Typography>
      <Paper elevation={3} sx={{py: 3, px: 2}}>
        <form onSubmit={onSubmit}>
          <Field form={form} attrs={SignInForm.password} />
          <Box mt={3} textAlign="center">
            <SubmitButton form={form}>Reset Password</SubmitButton>
          </Box>
        </form>
      </Paper>
    </Container>
  )
}
